import type {
  SearchParamsObject,
  SearchResponse,
} from '@algolia/client-search';
import { searchClient } from '@algolia/client-search';
import queryString from 'query-string';

import { postJson } from '#/utils/http/postJson';
import { invariant } from '#/utils/invariant';
import { RichError } from '#/utils/RichError';
import { trackError } from '#/utils/trackError';

import { AlgoliaGeneralError } from './AlgoliaGeneralError';
import {
  AlgoliaValidUntilError,
  isValidUntilError,
} from './AlgoliaValidUntilError';

export type AlgoliaSearchMultiQuery = {
  analytics?: boolean;
  clickAnalytics?: boolean;
  getRankingInfo?: boolean;
  indexName: string;
  options: SearchParamsObject;
  query: string;
};

type AlgoliaSearchMultiArgs = {
  config: Readonly<{ appId: string; apiKey: string }>;
  queries: AlgoliaSearchMultiQuery[];
  onApiKeyTimeout?: () => Promise<string>;
  isUsingAlgoliaMock: boolean;
};

export async function algoliaSearchMulti<TResponse>({
  config: { appId, apiKey },
  queries,
  onApiKeyTimeout,
  isUsingAlgoliaMock,
}: AlgoliaSearchMultiArgs) {
  if (isUsingAlgoliaMock) {
    // Callers destructure per-query results (e.g. the VOLOP org-count
    // query), so the mock must answer every query, not just the first.
    const results = await Promise.all(
      queries.map((query) =>
        postJson<SearchResponse<TResponse>>(
          `http://algoliamock:6577/${query.indexName}/search`,
          {
            query: query.query,
            hitsPerPage: query.options.hitsPerPage,
            page: query.options.page,
            facets: query.options.facets,
            filters: query.options.filters,
            attributesToHighlight: query.options.attributesToHighlight,
          },
          {
            headers: {
              'x-algolia-api-key': apiKey,
            },
          },
        ),
      ),
    );

    return { results };
  }
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');

  const requests = queries.map((q) => ({
    indexName: q.indexName,
    ...q.options,
    query: q.query ? q.query.trim() : '',
    getRankingInfo: q.getRankingInfo ?? true,
    clickAnalytics: q.clickAnalytics ?? true,
    analytics: q.analytics ?? true,
  }));

  let client = searchClient(appId, apiKey);

  let request = client.searchForHits<TResponse>({
    requests,
  });

  return request.catch((e) => {
    // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (isValidUntilError(e)) {
      if (onApiKeyTimeout) {
        return onApiKeyTimeout().then((newApiKey) => {
          client = searchClient(appId, newApiKey);
          request = client.searchForHits({ requests });
          return request;
        });
      }

      throw new AlgoliaValidUntilError();
    }

    const requestInfo = {
      // TODO: Fix this the next time the file is edited.
      // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      errorMessage: e.message,
      requestParams: queryString.stringify(requests),
      // TODO: Fix this the next time the file is edited.
      // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      statusCode: e.statusCode,
      // TODO: Fix this the next time the file is edited.
      // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      response: e.response,
    };

    trackError(
      new RichError('Algolia POST request error.', {
        requestInfo: JSON.stringify(requestInfo),
      }),
    );

    throw new AlgoliaGeneralError(
      `Algolia Error: ${JSON.stringify(requestInfo)}`,
    );
  });
}
